import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { MapPin, Star, Briefcase, Languages } from "lucide-react";

export type PublicProfile = {
  uid: string;
  role: "broker" | "consultant" | "partner" | "buyer" | "seller";
  firstName: string;
  lastName: string;
  photoURL?: string;
  firmName?: string;
  companyName?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  primarySector?: string;
  yearsExperience?: string;
  spokenLanguages?: string[];
  feeStructure?: string[];
  addons?: string[];
  status?: string;
  bio?: string;
  // Buyer-specific public fields
  sectors?: string[];           // sectors of interest
  experience?: string;          // acquisition experience text
  selfDescription?: string;     // self description
  budgetRange?: string;         // e.g. "$500K - $2M"
  subscriptionTier?: string;    // visitor | essential | essentialPlus
};

export function PublicProfileCard({
  profile,
  lang = "en",
  onContact,
  onViewFull,
}: {
  profile: PublicProfile;
  lang?: "en" | "fr";
  onContact?: (uid: string) => void;
  onViewFull?: (uid: string) => void;
}) {
  const initials =
    `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`.toUpperCase() || "?";

  const displayName = `${profile.firstName} ${profile.lastName}`.trim();

  const firmLabel = profile.firmName || profile.companyName || "";

  const location = [profile.city, profile.stateProvince, profile.country]
    .filter(Boolean)
    .join(", ");

  const roleLabel: Record<string, { en: string; fr: string }> = {
    broker: { en: "Broker", fr: "Courtier" },
    consultant: { en: "Consultant", fr: "Consultant" },
    partner: { en: "Internal Partner", fr: "Partenaire Interne" },
    buyer: { en: "Buyer", fr: "Acheteur" },
    seller: { en: "Seller", fr: "Vendeur" },
  };

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="flex flex-row items-start gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={profile.photoURL} alt={displayName} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base truncate">{displayName}</h3>
          {firmLabel && (
            <p className="text-sm text-muted-foreground truncate">{firmLabel}</p>
          )}
          <Badge variant="secondary" className="mt-1">
            {roleLabel[profile.role]?.[lang] || profile.role}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-2 text-sm">
        {location && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}
        {profile.primarySector && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Briefcase className="h-4 w-4 shrink-0" />
            <span className="truncate">{profile.primarySector}</span>
          </div>
        )}
        {profile.yearsExperience && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Star className="h-4 w-4 shrink-0" />
            <span>
              {profile.yearsExperience}{" "}
              {lang === "fr" ? "ans d'expérience" : "years of experience"}
            </span>
          </div>
        )}
        {Array.isArray(profile.spokenLanguages) &&
          profile.spokenLanguages.length > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Languages className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {profile.spokenLanguages.slice(0, 3).join(", ")}
              </span>
            </div>
          )}
        {Array.isArray(profile.addons) && profile.addons.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {profile.addons.map((a) => (
              <Badge key={a} variant="outline" className="text-xs">
                {a.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        )}
        {profile.bio && (
          <p className="text-muted-foreground line-clamp-3 pt-1">{profile.bio}</p>
        )}
      </CardContent>

      <CardFooter className="gap-2">
        {onViewFull && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onViewFull(profile.uid)}
          >
            {lang === "fr" ? "Voir le profil" : "View profile"}
          </Button>
        )}
        {onContact && (
          <Button
            size="sm"
            className="flex-1"
            onClick={() => onContact(profile.uid)}
          >
            {lang === "fr" ? "Contacter" : "Contact"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
